// sprintFlags.js — wire-dead-engines feature flags.
//
// Renderer-side flags for the newly-wired engines. This file INTENTIONALLY touches
// `window.localStorage`, so it is NOT a pure engine and is excluded from the
// `arch:lib-purity` test (which scopes only the execution/verify engines). Keeping the
// flags here (not in a pure lib) preserves the Dependency Rule.
//
// DEFAULT-ON contract (the kill-switch discipline): an ABSENT flag — or anything other
// than the literal '0'/'false' — means ENABLED, so a fresh install gets the wired
// behavior. Setting the flag to '0'/'false' restores byte-identical pre-sprint behavior.

export function readFlagDefaultOn(key) {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null || raw === undefined) return true;
    return !(raw === '0' || raw === 'false');
  } catch {
    return true; // private mode / quota → treat as default-on
  }
}

// Mirror of the above but DEFAULT-OFF: enabled ONLY when explicitly '1'/'true'. For UI that should be
// HIDDEN by default (the feature runs underneath; the panel is opt-in observability).
export function readFlagDefaultOff(key) {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === '1' || raw === 'true';
  } catch {
    return false;
  }
}

// E1 — lint/types/smoke verifiers in the AUTO_CORRECT gate.
export const readVerifiersDefaultEnabled = () => readFlagDefaultOn('apolo_verifiers_default');
// E3 — the DAG executor as the real driver on plan-approve (vs the prose turn).
export const readDagExecutorEnabled = () => readFlagDefaultOn('apolo_dag_executor');
// E4 — live checkpoints (save-on-green / restore-N-1) inside the DAG executor.
export const readCheckpointsEnabled = () => readFlagDefaultOn('apolo_checkpoints');
// RCA lentidão fix B — waves paralelas ATÔMICAS no executor DAG (tasks independentes
// juntas; rollback + fallback serial se algo falha). OFF ⇒ serial byte-idêntico.
export const readDagParallelEnabled = () => readFlagDefaultOn('apolo_dag_parallel');
// autolearn E1 — feed the DAG honest-green result into the learning ledger (was dropped).
export const readDagLearningEnabled = () => readFlagDefaultOn('apolo_dag_learning');
// autolearn follow-up — show the SELF-IMPROVE menu section (Rules/Session/Evaluator/Tests). DEFAULT OFF:
// the loop auto-runs without it (useEvaluatorAutoRun + worker crons), so the panels are hidden by default;
// flip apolo_show_selfimprove='1' to bring back the observability/override panels.
export const readShowSelfImproveEnabled = () => readFlagDefaultOff('apolo_show_selfimprove');

// ── arch-fable5 sprint flags ──────────────────────────────────────────────────────────────────────────
// A2 — honest-green for narration: audit user-facing progress claims against this turn's tool results.
// DEFAULT ON (purely additive — it only ANNOTATES unbacked claims; never blocks or inverts a verdict).
export const readProgressAuditEnabled = () => readFlagDefaultOn('apolo_progress_audit');
// P3-T01 (code-tab-lift) — the BLOCKING half of A2: when the narration claims tests/build
// PASS ('green' kind) but NO tool result backs it AND no oracle verifier ran green this turn,
// the loop nudges ONCE to actually verify/retract, and if the lie persists it blocks `done`
// with stopReason 'unbacked_green_claim'. DEFAULT ON, but SURGICAL: only the fabricated-green
// case fires — an honest done, a done with no green claim, or one backed by a passing
// test/oracle is byte-identical (the Engineering success path is untouched, L-3).
// harness-audit 07-13 — o mesmo apolo_done_claim_block gate cobre AMBAS as metades BLOCKING da
// família "unbacked claim": o green-claim (testes/build passaram sem verificador) E o write-claim
// (afirmou criar/editar arquivo sem tool de escrita). Nudge 1x (action_claim), e se a mentira
// persiste ⇒ block 'unbacked_write_claim'. Mesmo kill-switch, mesma família. Ver classifyReply §7b.
export const readDoneClaimBlockEnabled = () => readFlagDefaultOn('apolo_done_claim_block');
// harness-audit 07-13 — migrar prompt→scaffolding: o gate "editou sem rodar check" era
// default-OFF (a garantia vivia só no charter). Agora default-ON — nudge UMA vez (ranChecksNudged)
// quando o turno editou, não commitou e nenhum check rodou. Kill-switch '0'/'false' = comportamento anterior.
export const readRanChecksGateEnabled = () => readFlagDefaultOn('apolo_ran_checks_gate');
// A3 — fresh-context verifier: run the DAG acceptance check in a clean loop context. DEFAULT OFF (cost).
export const readFreshVerifierEnabled = () => readFlagDefaultOff('apolo_fresh_verifier');
// harness-uplift-r2 E1 — spec-gate-live: run the deterministic symbolic contradiction/gap
// check (detectContradictions) over a plan-turn spec before coding. DEFAULT OFF (behavior change;
// promote only after A/B proof — see _sprint/harness-uplift-r2). No-LLM-judge: verdict is pure logic.
export const readSpecGateLiveEnabled = () => readFlagDefaultOff('apolo_spec_gate_live');
// harness-uplift-r2 E2 — condenser: rebuild the model-facing View from an append-only event
// log (never mutate history) with a Condensation record + dual trigger. DEFAULT OFF (interacts
// with the proven cache boundary — promote only after A/B proves cacheRead does not drop).
export const readCondenserEnabled = () => readFlagDefaultOff('apolo_condenser');
// harness-uplift-r2 E3 — rule-activation-tiers: Cascade-style 4-mode activation
// (always_on/model_decision/glob/manual) for steering rules. DEFAULT OFF (today rules are
// binary always_on/glob; model_decision + manual are the new tiers). Byte-identical when OFF.
export const readRuleTiersEnabled = () => readFlagDefaultOff('apolo_rule_tiers');
// harness-uplift-r2 E4 — tool-result-clearing: clear the body of already-consumed tool
// results deep in history (keep last N verbatim) before compaction. DEFAULT OFF (rewrites
// the prefix → only safe on a non-cached block; promote only after A/B proves cacheRead holds).
export const readToolResultClearingEnabled = () => readFlagDefaultOff('apolo_tool_result_clearing');
// harness-uplift-r2 E6 — checks-and-notes (Amp Checks + Anthropic NOTES + agent.end guard).
// All DEFAULT OFF: checks_multi = 1 subagent per review criterion; agent_notes = persistent
// NOTES store; continue_guard = one-shot follow-up with an anti-loop marker.
export const readChecksMultiEnabled = () => readFlagDefaultOff('apolo_checks_multi');
export const readAgentNotesEnabled = () => readFlagDefaultOff('apolo_agent_notes');
export const readContinueGuardEnabled = () => readFlagDefaultOff('apolo_continue_guard');
// A4 — async Hermes sub-agents: steer/report channel on the fan-out. DEFAULT OFF (behavior change).
export const readHermesAsyncEnabled = () => readFlagDefaultOff('apolo_hermes_async');
// A7 — send_to_user verbatim delivery tool. DEFAULT OFF (new tool).
export const readSendToUserEnabled = () => readFlagDefaultOff('apolo_send_to_user');

// ── role-scaffold-twv (prescribed Thinker→Worker→Verifier over the DAG executor) ───────────────────────
// Wrap the per-task runTask seam with a prescribed Thinker→Worker scaffold (Verifier stays the oracle).
// The role pool is the Council roster. DEFAULT OFF (behavior change + extra Thinker call): OFF ⇒
// seams.runTask === baseRunTask and no thinkerNote is ever set ⇒ byte-identical to today.
export const readRoleScaffoldEnabled = () => readFlagDefaultOff('apolo_role_scaffold');

// ── no-silent-green (LAW improvement: close the noVerifiers==green soft spot) ──────────────────────────
// When ON, a DAG task whose acceptance matched NO project verifier gets a real `node --check` syntax floor
// on its changed .js files (M2); if still unverifiable, its verdict is LABELED 'unverified' (M1) instead of
// silently green. DEFAULT ON (E0-WS1-T02, 01-DOCTRINE Lei #4): a fresh install closes the
// `noVerifiers→green` hole out of the box. Setting '0'/'false' is the kill-switch (byte-identical to
// pre-sprint: noVerifiers→green). Preserves the LAW: no confidence/score gate — only a real executable
// verifier + an honest label.
export const readNoSilentGreenEnabled = () => readFlagDefaultOn('apolo_no_silent_green');

// apolo_council_hide flag — DEFAULT OFF (feature stays opt-in observability).
// T03 (tier-s-sprint-unified-2026-06-28).
export const readCouncilHideEnabled = () => readFlagDefaultOff('apolo_council_hide');

// ── harness-live-verify sprint flags (Worker-side gates, forwarded per turn) ───────────────────────────
// E2 — CACHE_BOUNDARY no assembler do mergedSystem (stable-first). PROVADO AO VIVO 2026-07-14
// (kimi/kimi-k2.5: cache_read_tokens 11520 ON vs 4096 OFF no tail) ⇒ DEFAULT ON. Kill-switch
// '0'/'false' volta à ordem legada byte-idêntica. (const local: só o collector consome.)
const readCacheBoundaryEnabled = () => readFlagDefaultOn('apolo_cache_boundary');
// E1 — tool_use/tool_result NATIVO no path Anthropic (anthropicThinking). DEFAULT OFF até a prova
// ao vivo (sessão Anthropic 2+ tools sem 400). OFF ⇒ text-fold de hoje. Exportado: o agentLoop
// injeta esta flag no batchBuffer (core loop/ não pode importar flags — arch:framework-free).
export const readNativeAnthropicToolsEnabled = () => readFlagDefaultOff('apolo_native_anthropic_tools');
// Junta as flags Worker-bound LIGADAS pro corpo do agent-turn. null quando nenhuma está ON ⇒ o
// campo `flags` nem entra no body (net-zero: corpo byte-idêntico ao de hoje).
const readToolLeanEnabled = () => readFlagDefaultOff('apolo_tool_lean');
export function collectWorkerTurnFlags() {
  const flags = {};
  if (readCacheBoundaryEnabled()) flags.apolo_cache_boundary = true;
  if (readNativeAnthropicToolsEnabled()) flags.apolo_native_anthropic_tools = true;
  // harness-uplift-r2 E5 — tool-retrieval-lean: rank+cap tools Worker-side by relevance
  // (core/guardrail never dropped). DEFAULT OFF; promote only after A/B proves no green regress.
  if (readToolLeanEnabled()) flags.apolo_tool_lean = true;
  return Object.keys(flags).length > 0 ? flags : null;
}

// ── code-tab-to-9 E3 flags ──────────────────────────────────────────────────────────────────────────
// E3-WS1-T01 — acceptance run-command: when ON and no acceptance-specific verifier compiled, drive the
// project's own test/lint scripts as the verification floor for ANY file type (not just .js node --check).
// DEFAULT OFF (OFF == today: falls through to E1 default verifiers, byte-identical).
export const readAcceptanceRunCommandEnabled = () => readFlagDefaultOff('apolo_acceptance_run_command');
// E3-WS2-T01 — checkpoint gate at the loop boundary: capture on honest-green, restore on fail.
// DEFAULT ON (code-tab-to-10 sprint): a fresh install gets live checkpointing out of the box.
// Setting '0'/'false' is the kill-switch (byte-identical to pre-sprint behavior).
export const readCheckpointLoopEnabled = () => readFlagDefaultOn('apolo_checkpoint_loop');
// E3-WS4-T01 — file-deletion / external-write guardrail over evaluateToolPolicy.
// DEFAULT ON (code-tab-to-10 sprint): blocks auto-rm/del/shred/unlink via run_command and
// write_file/apply_edit to paths outside the project root. Setting '0'/'false' is the kill-switch.
export const readToolGuardrailEnabled = () => readFlagDefaultOn('apolo_tool_guardrail');
// code-quality PR12 — edit-vs-green ledger ENFORCEMENT. DEFAULT OFF (felt-pain
// lever): the ledger always records, but only when this is ON does a stale verdict
// (file edited after its last green) count as !reachedGreen in the done gate. Off ==
// byte-identical to today (the existing honest-green checks are unchanged either way).
export const readGreenLedgerEnforced = () => readFlagDefaultOff('apolo_green_ledger');

// ── super-marathon sprint flags ───────────────────────────────────────────────────────────────────────
// These VISIBLE, render-only features are DEFAULT ON (a fresh install SEES them) — flip to '0'/'false'
// to hide. They only surface data / new UI; none change the agent-loop or tool behavior.
// E2 — verification SEAL (Chat seal + Home "última entrega" card).
export const readVerificationReportEnabled = () => readFlagDefaultOn('apolo_verification_report');
// E8 — lifecycle hooks (preTool/postTool/stop). DEFAULT OFF: this one CHANGES tool behavior (a blocking
// hook can veto a tool) + hits the hook API, so it stays opt-in. OFF ⇒ byte-identical loop.
export const readHooksEnabled = () => readFlagDefaultOff('apolo_hooks_lifecycle');
// E7 — unified Customize hub ("Customizar (tudo)" no menu). DEFAULT ON.
export const readCustomizeHubEnabled = () => readFlagDefaultOn('apolo_customize_hub');
// E5 — readable ("Ver legível") skill-artifact preview in the Learning panel. DEFAULT ON.
export const readSkillPreviewEnabled = () => readFlagDefaultOn('apolo_skill_preview');
// E6 — cost breakdown panel (per-provider / per-model spend). DEFAULT ON.
export const readCostBreakdownEnabled = () => readFlagDefaultOn('apolo_cost_breakdown');
// E3 — multi-profile switcher + create/Copy-setup in the UserMenu. DEFAULT ON.
export const readMultiProfileEnabled = () => readFlagDefaultOn('apolo_multi_profile');
// harness-audit 07-13 (EPIC-CLASSIFY-1) — classificador de permissão SUBORDINADO no autoReview
// (etapa 5, banda ambígua). DEFAULT OFF: é opt-in porque adiciona 1 chamada LLM barata por tool
// ambígua. OFF ⇒ o seam opts.classify fica vazio = comportamento 100% determinístico de hoje
// (net-zero). ON só reduz prompts humanos na banda "ask"; nunca sobrepõe regra determinística
// (etapas 1-4 vencem) e tem fail-safe (3 blocks seguidos/20 no run ⇒ volta ao humano). Ver ADR-007.
export const readPermClassifierEnabled = () => readFlagDefaultOff('apolo_perm_classifier');
